"use client";

import { useMemo, useState } from "react";
import NextLink from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  Button,
  HStack,
  Link as ChakraLink,
  Menu,
  Portal,
  Text,
} from "@chakra-ui/react";
import {
  PAGE_PATH_ADMIN,
  PAGE_PATH_BACKLOG,
  PAGE_PATH_STUDIO,
} from "@/shared/config/app";
import { useOptionItems } from "@/shared/ui/controls/useOptionItems";
import { usePageSearch } from "@/features/search";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import WithMenuItem from "@/shared/ui/actions/WithMenuItem";

function resolveActiveWorkspace(pathname: string | null) {
  if (pathname?.startsWith("/admin")) {
    return PAGE_PATH_ADMIN;
  }

  if (pathname?.startsWith("/backlog")) {
    return PAGE_PATH_BACKLOG;
  }

  return PAGE_PATH_STUDIO;
}

export default function PrivateWorkspaceSwitcher() {
  const pathname = usePathname();
  const router = useRouter();
  const { pagePathItems } = useOptionItems();
  const { resolvePathname } = usePageSearch();
  const [mounted, setMounted] = useState(false);

  const items = pagePathItems(true);
  const activeWorkspace = resolveActiveWorkspace(pathname);
  const activeItem = useMemo(
    () => items.find((item) => item.value === activeWorkspace) ?? items[0],
    [activeWorkspace, items],
  );

  const setMountedRef = (el: HTMLButtonElement | null) => {
    if (el && !mounted) {
      setMounted(true);
    }
  };

  return (
    <>
      <HStack display={{ base: "none", md: "flex" }} gap="1.5" align="center">
        {items.map((item) => {
          const isActive = item.value === activeWorkspace;

          return (
            <ChakraLink
              key={item.value}
              asChild
              px="3"
              py="2"
              borderRadius="full"
              fontWeight={isActive ? "semibold" : "medium"}
              color={isActive ? "moveon.primary" : "moveon.shell.mutedFg"}
              bg={isActive ? "moveon.shell.activeBg" : "transparent"}
              borderWidth="1px"
              borderColor={isActive ? "moveon.shell.border" : "transparent"}
              _hover={{
                textDecoration: "none",
                bg: "moveon.shell.hoverBg",
                color: "moveon.primary",
                borderColor: "moveon.shell.border",
              }}
              _focus={{ boxShadow: "none", outline: "none" }}
              transition="all 0.2s"
            >
              <NextLink href={resolvePathname(item.value)}>
                {item.label}
              </NextLink>
            </ChakraLink>
          );
        })}
      </HStack>

      {!mounted ? (
        <Button
          ref={setMountedRef}
          variant="ghost"
          size="sm"
          display={{ base: "inline-flex", md: "none" }}
          px="3"
          borderRadius="full"
          borderWidth="1px"
          borderColor="moveon.shell.border"
          color="moveon.surface.fg"
          bg="moveon.shell.bg"
        >
          <HStack gap="2" w="full" justify="space-between">
            <Text fontSize="sm" fontWeight="semibold" truncate>
              {activeItem?.label ?? "Workspace"}
            </Text>
            <GenericIcon icon="slash_forward" size="sm" />
          </HStack>
        </Button>
      ) : null}

      {mounted ? (
        <Menu.Root
          positioning={{ placement: "bottom-start" }}
          lazyMount={true}
          size="sm"
        >
          <Menu.Trigger asChild>
            <Button
              variant="ghost"
              size="sm"
              display={{ base: "inline-flex", md: "none" }}
              px="3"
              borderRadius="full"
              borderWidth="1px"
              borderColor="moveon.shell.border"
              color="moveon.surface.fg"
              bg="moveon.shell.bg"
            >
              <HStack gap="2" w="full" justify="space-between">
                <Text fontSize="sm" fontWeight="semibold" truncate>
                  {activeItem?.label ?? "Workspace"}
                </Text>
                <GenericIcon icon="slash_forward" size="sm" />
              </HStack>
            </Button>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content
                bg="moveon.shell.bg"
                color="moveon.surface.fg"
                borderWidth="1"
                borderColor="moveon.shell.border"
                boxShadow="lg"
                minW="14rem"
              >
                <Menu.ItemGroup>
                  <Menu.ItemGroupLabel
                    color="moveon.shell.mutedFg"
                    fontSize="sm"
                    fontWeight="semibold"
                  >
                    Workspace
                  </Menu.ItemGroupLabel>
                  {items.map((item) => (
                    <WithMenuItem
                      key={item.value}
                      fontSize="md"
                      value={item.value}
                      humanReadable={item.description}
                      onClick={() => router.push(resolvePathname(item.value))}
                    >
                      {item.label}
                    </WithMenuItem>
                  ))}
                </Menu.ItemGroup>
                <Menu.Separator />
                <WithMenuItem
                  fontSize="md"
                  value="home"
                  humanReadable="Go to the public home page"
                  onClick={() => router.push("/")}
                >
                  Home
                </WithMenuItem>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      ) : null}
    </>
  );
}
